import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Circle, Search, PackageSearch } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { formatApiError } from "@/pages/AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Brief Received", "Producer Reviewing", "Contact & Next Steps"];
const STATUS_INDEX = { new: 1, contacted: 2, closed: 3 };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function TrackPage() {
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(urlCode || "");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookup = async (c) => {
    if (!c.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/track/${c.trim()}`);
      setInfo(data);
    } catch (err) {
      setInfo(null);
      setError(err.response?.status === 404 ? "No brief found with that code. Double-check and try again." : formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlCode) lookup(urlCode);
  }, [urlCode]);

  const reached = info ? STATUS_INDEX[info.status] || 1 : 0;

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            data-testid="track-home-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5"
          >
            <LogoMark className="w-8 h-8" />
            <span className="font-display font-extrabold tracking-tight">
              VERTEX<span className="text-crimson">.</span>
            </span>
          </button>
          <button
            type="button"
            data-testid="track-back-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-xs text-gray-400 hover:text-crimson transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-14">
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Track Your Brief</h1>
        <p className="text-gray-500 text-sm mt-3 leading-relaxed">
          Enter the tracking code you received when you submitted your requirement — no account needed.
        </p>

        {!urlCode && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              navigate(`/track/${code.trim()}`, { replace: true });
              lookup(code);
            }}
            className="mt-8 flex gap-3"
            data-testid="track-form"
          >
            <input
              data-testid="track-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="VP-XXXXXX"
              className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-sm text-gray-900 font-mono placeholder:text-gray-400 outline-none focus:border-crimson/60 focus:ring-2 focus:ring-crimson/15 transition-all"
            />
            <button
              type="submit"
              data-testid="track-submit-button"
              disabled={loading}
              className="btn-glow inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-crimson text-white font-bold text-sm disabled:opacity-60"
            >
              <Search className="w-4 h-4" />
              {loading ? "Checking…" : "Track"}
            </button>
          </form>
        )}

        {error && (
          <div
            className="mt-8 rounded-2xl border border-crimson/30 bg-crimson/5 p-5 text-sm text-gray-800"
            data-testid="track-error-panel"
          >
            {error}
          </div>
        )}

        {info && (
          <div
            className="mt-8 rounded-3xl border border-gray-100 bg-white shadow-soft p-7 md:p-9"
            data-testid="track-status-card"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-[11px] tracking-[0.25em] text-crimson uppercase">
                  {info.track_code}
                </div>
                <h2 className="font-display font-bold text-2xl mt-2 text-gray-900">
                  {info.first_name} {info.last_name}
                </h2>
                <p className="text-gray-500 text-sm mt-1">{info.company}</p>
              </div>
              <span className="font-mono text-[10px] tracking-widest text-gray-400 uppercase">
                {fmtDate(info.created_at)}
              </span>
            </div>

            <div className="mt-8 space-y-0">
              {STEPS.map((s, i) => {
                const done = i + 1 <= reached;
                const current = i + 1 === reached;
                return (
                  <div key={s} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      {done ? (
                        <CheckCircle2 className={`w-5 h-5 ${current ? "text-crimson" : "text-emerald-500"}`} />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300" />
                      )}
                      {i < STEPS.length - 1 && (
                        <span className={`w-px flex-1 min-h-[28px] my-1 ${done ? "bg-crimson/40" : "bg-gray-200"}`} />
                      )}
                    </div>
                    <div className={`pb-7 ${current ? "text-gray-900" : done ? "text-gray-600" : "text-gray-300"}`}>
                      <div className="text-sm font-semibold">{s}</div>
                      {current && info.status === "new" && i === 0 && (
                        <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                          Your brief is with our producers. Expect a reply within 24 hours.
                        </p>
                      )}
                      {current && info.status === "contacted" && (
                        <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                          Our producer has reached out — check your email.
                        </p>
                      )}
                      {current && info.status === "closed" && (
                        <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                          This brief is closed. Want more videos? Submit a new brief anytime.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <div>
                <div className="font-mono text-[10px] tracking-widest text-gray-400 uppercase">Looking For</div>
                <div className="text-sm text-gray-800 mt-1">{info.service}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-widest text-gray-400 uppercase">Budget (INR)</div>
                <div className="text-sm text-gray-800 mt-1 font-mono">₹ {info.budget}</div>
              </div>
            </div>
          </div>
        )}

        {!info && !error && !urlCode && (
          <div
            className="mt-10 rounded-3xl border border-dashed border-gray-200 p-8 flex flex-col items-center gap-3 text-center"
            data-testid="track-empty-hint"
          >
            <PackageSearch className="w-7 h-7 text-gray-300" />
            <p className="text-gray-400 text-sm max-w-sm">
              Your code looks like <span className="font-mono text-gray-600">VP-7K3D9Q</span> and was shown after you
              submitted your brief.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
